import { useState, useCallback } from 'react';

/**
 * Hook for rejecting hiring offers
 * @param {Object} options - Configuration options
 * @param {number} options.hiringId - Hiring ID
 * @param {Function} options.onSuccess - Optional callback called after successful reject (receives result data)
 * @param {Function} options.onError - Optional callback called on error (receives error message)
 * @returns {Object} Reject hiring state and handler function
 */
const useRejectHiring = ({ hiringId, onSuccess, onError }) => {
  const [isRejecting, setIsRejecting] = useState(false);
  const [error, setError] = useState(null);

  const handleReject = useCallback(async () => {
    if (!hiringId) {
      const errorMsg = 'No se puede rechazar: falta el ID de la oferta';
      setError(errorMsg);
      if (onError) onError(errorMsg);
      return { success: false, error: errorMsg };
    }

    setIsRejecting(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_STAGING_URL}/api/hirings/${hiringId}/reject`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}), // Send empty JSON object to satisfy middleware
      });

      if (response.ok) {
        const data = await response.json();
        const result = { success: true, data };
        if (onSuccess) onSuccess(result);
        return result;
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.message || 'No se pudo rechazar la oferta';
        setError(errorMessage);
        if (onError) onError(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      console.error('Error rejecting hiring offer:', error);
      const errorMessage = 'Error al rechazar la oferta';
      setError(errorMessage);
      if (onError) onError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsRejecting(false);
    }
  }, [hiringId, onSuccess, onError]);

  return {
    handleReject,
    isRejecting,
    error
  };
};

export default useRejectHiring;

